import { useEffect, useSyncExternalStore } from 'react';

export type BreadcrumbNode = {
  label: string;
  to?: string;
  onClick?: () => void;
};

type Listener = () => void;

let tail: BreadcrumbNode[] = [];
const listeners = new Set<Listener>();

const setTail = (nodes: BreadcrumbNode[]) => {
  tail = nodes;
  listeners.forEach(listener => listener());
};

const subscribe = (listener: Listener) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const getTail = () => tail;

/**
 * Lets a page deep in a plugin's own route tree append breadcrumb levels the
 * URL alone can't express (e.g. Operations, where project/request selection
 * is component state, not a URL param). Lives here rather than in
 * `packages/app` because plugins may not depend on the app package — this is
 * the one piece of the breadcrumb feature both sides need to share. A plain
 * module-level store rather than React Context: the contributing page and
 * the top bar rendering the crumbs are siblings under Backstage's extension
 * tree, not a shared component subtree, so there is no single ancestor to
 * hang a `<Provider>` from. No dependency array on the effect below — it
 * re-syncs the store on every render of the caller so a stale closure (e.g.
 * an `onClick` over last-fetched state) never lingers; cleared on unmount.
 */
export const useSetBreadcrumbTail = (nodes: BreadcrumbNode[]) => {
  useEffect(() => {
    setTail(nodes);
    return () => setTail([]);
  });
};

/** Consumed by the top bar to read the current page-contributed tail. */
export const useBreadcrumbTail = (): BreadcrumbNode[] =>
  useSyncExternalStore(subscribe, getTail, getTail);
